import Link from "next/link";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import type { ParagraphHeroProduct as ParagraphHeroProductData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import styles from "./ParagraphHeroProduct.module.css";

/**
 * Layout 50 - `PARAGRAPH_HERO_PRODUCT_LAYOUT`: hero product.
 *
 * Struktura:
 *   - Light full-bleed bg (--color-surface-hero)
 *   - 2 kolumny:
 *     - Lewa: eyebrow (text_1) + H1 (name) + description (text HTML) + 3 CTA (buttons)
 *       + podpis (text_2 plain)
 *     - Prawa: obrazek/screenshot (image URL z /boxes/1/)
 *   - Buttony: pierwszy primary (accent), reszta outline. Badge (np. "WKROTCE")
 *     jako suffix przy etykiecie.
 *
 * Typowo: hero "Nasz produkt" / feature launch strony.
 */
export function ParagraphHeroProduct({
  paragraph,
  t: _t,
}: {
  paragraph: ParagraphHeroProductData;
  t: ParagraphTranslations;
}) {
  const {
    text_1: eyebrow,
    name: title,
    text: description,
    buttons,
    text_2: footnote,
    image,
  } = paragraph;

  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasDescription = description && description.trim() !== "";
  const hasFootnote = footnote && footnote.trim() !== "";
  const hasImage = image && image.trim() !== "";

  return (
    <section className={styles.section}>
      <div className={styles.grid}>
        <div className={styles.body}>
          {hasEyebrow ? <span className={styles.eyebrow}>{eyebrow}</span> : null}
          {title ? <h2 className={styles.title}>{title}</h2> : null}
          {hasDescription ? (
            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : null}

          {buttons.length > 0 ? (
            <div className={styles.buttons}>
              {buttons.map((btn, idx) => {
                const variantClass =
                  idx === 0 ? styles.btnPrimary : idx === 1 ? styles.btnOutline : styles.btnMuted;
                const internal = btn.url ? extractInternalHref(btn.url) : null;
                const label = (
                  <>
                    {btn.label}
                    {btn.badge ? (
                      <span className={styles.btnBadge}>{btn.badge}</span>
                    ) : null}
                  </>
                );

                //Tooltip aktywny gdy jest opis LUB CTA. Wzorzec z ParagraphProductCta:
                //wrapper .btnWrap ma :hover / :focus-within na tooltipie.
                const hasTooltipCta = btn.tooltip_cta_label.trim() !== "" && btn.tooltip_cta_url.trim() !== "";
                const hasTooltip = btn.tooltip_desc.trim() !== "" || hasTooltipCta;

                //Bez URL: <span role="button"> zamiast link (np. "WKROTCE" bez celu).
                const btnEl = !btn.url ? (
                  <span
                    className={`${styles.btn} ${variantClass}`}
                    role="button"
                    tabIndex={hasTooltip ? 0 : undefined}
                  >
                    {label}
                  </span>
                ) : internal !== null ? (
                  <Link href={internal} className={`${styles.btn} ${variantClass}`}>
                    {label}
                  </Link>
                ) : (
                  <a href={btn.url} className={`${styles.btn} ${variantClass}`}>
                    {label}
                  </a>
                );

                if (!hasTooltip) return <span key={idx}>{btnEl}</span>;

                return (
                  <div key={idx} className={styles.btnWrap}>
                    {btnEl}
                    <div className={styles.tooltip} role="tooltip">
                      {btn.tooltip_desc ? (
                        <p className={styles.tooltipDesc}>{btn.tooltip_desc}</p>
                      ) : null}
                      {hasTooltipCta ? (
                        <a href={btn.tooltip_cta_url} className={styles.tooltipCta}>
                          {btn.tooltip_cta_label}
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {hasFootnote ? <p className={styles.footnote}>{footnote}</p> : null}
        </div>

        {hasImage ? (
          <div className={styles.mediaWrap}>
            <img src={image} alt={title ?? ""} className={styles.media} loading="lazy" />
          </div>
        ) : null}
      </div>
    </section>
  );
}
